import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { enqueueSnackbar } from 'notistack';

import { axiosClient } from '@/services/BaseApi.service';

import type {
  CanaryTokenCreateDTO,
  CanaryTokenDTO,
  CanaryTokenUpdateDTO
} from './AdminCanaryTokens.types';

export const useCanaryTokens = (organizationLabel: string) => {
  const queryClient = useQueryClient();
  const CANARY_TOKENS_QUERY_KEY = `organizations/${organizationLabel}/canary_tokens`;

  const { data, isLoading, error } = useQuery<CanaryTokenDTO[]>({
    queryKey: [CANARY_TOKENS_QUERY_KEY],
    enabled: !!organizationLabel
  });

  const createToken = useMutation({
    mutationFn: async (tokenData: CanaryTokenCreateDTO) => {
      const response = await axiosClient.post('/canary_tokens/', tokenData);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [CANARY_TOKENS_QUERY_KEY]
      });
      enqueueSnackbar('Canarytoken created successfully', {
        variant: 'success'
      });
    },
    onError: (err: AxiosError<{ detail: string }>) => {
      enqueueSnackbar(
        err.response?.data?.detail || 'Failed to create Canarytoken',
        {
          variant: 'error'
        }
      );
    }
  });

  const updateToken = useMutation({
    mutationFn: async ({
      id,
      data: tokenData
    }: {
      id: number;
      data: CanaryTokenUpdateDTO;
    }) => {
      const response = await axiosClient.put(`/canary_tokens/${id}`, tokenData);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [CANARY_TOKENS_QUERY_KEY]
      });
      enqueueSnackbar('Canarytoken updated successfully', {
        variant: 'success'
      });
    },
    onError: (err: AxiosError<{ detail: string }>) => {
      enqueueSnackbar(
        err.response?.data?.detail || 'Failed to update Canarytoken',
        {
          variant: 'error'
        }
      );
    }
  });

  const deleteToken = useMutation({
    mutationFn: async (id: number) => {
      await axiosClient.delete(`/canary_tokens/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [CANARY_TOKENS_QUERY_KEY]
      });
      enqueueSnackbar('Canarytoken deleted successfully', {
        variant: 'success'
      });
    },
    onError: (err: AxiosError<{ detail: string }>) => {
      enqueueSnackbar(
        err.response?.data?.detail || 'Failed to delete Canarytoken',
        {
          variant: 'error'
        }
      );
    }
  });

  return {
    data,
    isLoading,
    error,
    createToken,
    updateToken,
    deleteToken
  };
};

export default useCanaryTokens;
